import { Component, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { Subject, takeUntil } from 'rxjs';
import { InactivityService } from './core/auth/inactivity.service';
import { SessionWatchService } from './core/auth/session-watch.service';

@Component({
    selector   : 'app-root',
    templateUrl: './app.component.html',
    styleUrls  : ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy
{
    private readonly _destroying$ = new Subject<void>();

    /**
     * Constructor
     */
    constructor(
        private msalService: MsalService,
        private msalBroadcastService: MsalBroadcastService,
        private inactivityService: InactivityService,
        private sessionWatchService: SessionWatchService,
        private router: Router
    )
    {
    }

    /**
     * On init
     */
    ngOnInit(): void
    {
        // La vista del trabajador (Formación) corre dentro de un iframe de la
        // misma app: la sesión la cuida la ventana de afuera. Si esta copia
        // también vigilara inactividad, podría cerrar la sesión (compartida en
        // localStorage) mientras el usuario trabaja en la ventana principal.
        const vistaPreviaEmbebida = window.self !== window.top
            && window.location.pathname.startsWith('/formacion/vista-previa');

        // Vigilancia de sesión: fuera del init de MSAL a propósito, para que
        // siga funcionando aunque MSAL falle al inicializar (ese catch de abajo
        // solo loguea y sigue).
        if (!vistaPreviaEmbebida) {
            this.sessionWatchService.startWatching();
        }

        // Inicializar MSAL
        this.msalService.instance.initialize().then(() => {

            // Iniciar el monitoreo de inactividad después de inicializar MSAL
            if (!vistaPreviaEmbebida) {
                this.inactivityService.startMonitoring();
            }
        }).catch(error => {
            console.error('Error al inicializar MSAL:', error);
        });

        // Auto-recuperación de chunks viejos tras un deploy: si el index.html
        // cacheado pide un bundle lazy con hash que ya no existe (Amplify hace
        // deploys atómicos y borra los anteriores), la navegación muere sin
        // NavigationEnd y el splash queda para siempre — el usuario tenía que
        // dar Ctrl+R a mano. Aquí recargamos nosotros, una sola vez por intento
        // (flag en sessionStorage para no ciclar si el error persiste).
        this.router.events
            .pipe(takeUntil(this._destroying$))
            .subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    // Navegación sana: rearmar el flag para futuros deploys
                    sessionStorage.removeItem('recarga_chunk_viejo');
                    return;
                }
                if (event instanceof NavigationError) {
                    const err: any = event.error;
                    const mensaje = `${err?.message ?? err ?? ''}`;
                    const esChunkViejo = err?.name === 'ChunkLoadError'
                        || /Loading chunk|ChunkLoadError|dynamically imported module/i.test(mensaje);
                    if (esChunkViejo && !sessionStorage.getItem('recarga_chunk_viejo')) {
                        sessionStorage.setItem('recarga_chunk_viejo', '1');
                        window.location.reload();
                    }
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._destroying$.next();
        this._destroying$.complete();
    }
}
